import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import supplierService from '../../../services/supplierService';
import { format } from 'date-fns';

const RevenueTrendsChart = ({ language, supplierId, startDate, endDate }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const content = {
    en: {
      title: 'Revenue Trends Over Time',
      revenue: 'Revenue (USD)',
      orders: 'Orders',
      noData: 'No revenue data available for this period'
    },
    zh: {
      title: '收入趋势',
      revenue: '收入（美元）',
      orders: '订单',
      noData: '此期间没有收入数据'
    }
  };

  const t = content?.[language];

  useEffect(() => {
    loadTrendsData();
  }, [supplierId, startDate, endDate]);

  const loadTrendsData = async () => {
    try {
      setLoading(true);
      const trends = await supplierService?.getRevenueTrends(
        supplierId,
        format(startDate, 'yyyy-MM-dd'),
        format(endDate, 'yyyy-MM-dd')
      );
      
      const chartData = trends?.map(item => ({
        date: format(new Date(item?.date), 'MMM dd'),
        revenue: Number(item?.revenue)?.toFixed(2),
        orders: item?.orders
      }));
      
      setData(chartData);
    } catch (error) {
      console.error('Failed to load revenue trends:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-64">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="TrendingUp" size={20} className="text-primary" />
        <h3 className="text-lg font-heading font-semibold text-foreground">{t?.title}</h3>
      </div>
      {data?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{t?.noData}</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="date" stroke="var(--color-muted-foreground)" />
            <YAxis yAxisId="left" stroke="var(--color-primary)" />
            <YAxis yAxisId="right" orientation="right" stroke="var(--color-secondary)" />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--color-card)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px'
              }}
            />
            <Legend />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="revenue"
              stroke="var(--color-primary)"
              strokeWidth={2}
              name={t?.revenue}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="orders"
              stroke="var(--color-secondary)"
              strokeWidth={2}
              name={t?.orders}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default RevenueTrendsChart;